import { useEffect, type FC } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import clsx from "clsx";
import AdminSidebar from "@/shared/components/admin/AdminSidebar/AdminSidebar";
import Spacing from "@/shared/components/ui/Spacing";
import { useAdminBackground } from "@/core/hooks/useAdminBackground";
import styles from "./AdminBackgroundLayout.module.scss";
import AdminDashboardHeader from "@/shared/components/admin/AdminDashboardHeader/AdminDashboardHeader";
import { clearUser, getUser } from "@/store/slices/auth";
import { useAppSelector } from "@/store/hooks";
import { useAppDispatch } from "@/store/hooks";
import { useLogout } from "@/features/auth";
import { toastService } from "@/core/services/toast.service";
import { logger } from "@/core";
interface AdminLayoutProps {
  imageOpacity?: number;
  transitionDuration?: number;
}

const AdminLayout: FC<AdminLayoutProps> = () => {
  const navigate = useNavigate();
  const user = useAppSelector(getUser);
  const dispatch = useAppDispatch();
  const logoutMutation = useLogout();
  // Get background styles and state
  const { backgroundStyles, isImageLoaded } = useAdminBackground();
  logger.info("user", user);
  const handleLogout = async (): Promise<void> => {
    // Remove cookie
    toastService.info("Logout", "Logout Processing");
    await logoutMutation.mutateAsync();
    dispatch(clearUser());
    navigate("/");
    // Reload the page after navigation
    window.location.reload();
    return;
  };
  // ✅ SOLUTION: Use useEffect for all navigation logic
  useEffect(() => {
    // No user → redirect to login
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }

    // Teachers and regular users can't access admin layout
    if (user.role === "teacher" || user.role === "user") {
      navigate("/notFound", { replace: true });
      return;
    }

    // Super admin with YubiKey enabled needs 2FA
    if (
      user.role === "super_admin" &&
      user.isYubiKeyEnabled &&
      !user.isYubiKeyAuthenticated
    ) {
      navigate("/admin/verifyTwoFactorAuth", { replace: true });
      return;
    }

    // For super admin without YubiKey, stay on this page (dashboard)
    // No navigation needed
  }, [user, navigate]); // Only re-run when user or navigate changes

  return (
    <Spacing
      className={clsx(styles.layout, {
        [styles.imageLoaded]: isImageLoaded,
      })}
      style={backgroundStyles}
      display="flex"
      direction="column"
      height="100vh"
    >
      <div className={styles.gradientOverlay} />
      <AdminDashboardHeader isRightSectionShown={true} />

      {/* Content container that takes remaining space */}
      <Spacing
        display="flex"
        className={styles.contentContainer} // Add this class
      >
        <AdminSidebar onLogout={handleLogout} />
        <Spacing as="main" className={styles.main}>
          <Outlet />
        </Spacing>
      </Spacing>
    </Spacing>
  );
};

export default AdminLayout;
